/**
 * Overlay SVG da gênese gráfica (E26.F2/ADR-0037). // ux-neutrality:allow doc-comment
 *
 * Desenha os três tipos de marcador sobre a imagem da assinatura com // ux-neutrality:allow doc-comment
 * cor/forma POR TIPO — paleta própria, distinta da paleta de veredito dos // ux-neutrality:allow doc-comment
 * marcadores por critério (ADR-0037 §C): instrumentação neutra, nenhuma // ux-neutrality:allow doc-comment
 * semântica de suporte a hipótese. viewBox = dimensões naturais (mesmo // ux-neutrality:allow doc-comment
 * padrão do ContestedMarkerOverlay), escala junto com o <img>. // ux-neutrality:allow doc-comment
 */
import { ptBR } from '@/i18n/pt-BR';
import type { GenesisPointData, GraphicGenesisData } from '@/lib/case_signatures';
import { TOKEN_HEX } from '@/lib/design_tokens';

export interface GenesisOverlayProps {
  genesis: GraphicGenesisData;
}

/**
 * Paleta POR TIPO (ADR-0037 §C) — sem verde/vermelho/âmbar do veredito.
 *
 * E54.F6.T07: os três valores passam a ser degraus do sistema (aço, aço escuro
 * e grafite) em vez do azul/violeta/teal que sobreviveram à troca de paleta da
 * F1 porque vivem em atributo de apresentação de SVG, onde `var()` não resolve.
 * A separação exigida pela ADR-0037 continua de pé: nenhum deles é da rampa de
 * sinal, e a forma do marcador é quem carrega o tipo.
 */
export const GENESIS_TYPE_COLOR = {
  extremities: TOKEN_HEX.accent,
  junctions: TOKEN_HEX.accent900,
  estimated_stops: TOKEN_HEX.fgDim,
} as const;

function Diamond({
  point,
  size,
  stroke,
}: {
  point: GenesisPointData;
  size: number;
  stroke: number;
}): JSX.Element {
  const { x, y } = point;
  const d = size;
  return (
    <polygon
      points={`${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`}
      fill={GENESIS_TYPE_COLOR.junctions}
      fillOpacity={0.25}
      stroke={GENESIS_TYPE_COLOR.junctions}
      strokeWidth={stroke}
    >
      <title>{ptBR.workspaceCaso.genesisJunctions}</title>
    </polygon>
  );
}

export function GenesisOverlay({ genesis }: GenesisOverlayProps): JSX.Element {
  const { image_width, image_height } = genesis;
  const radius = Math.max(3, Math.round(Math.max(image_width, image_height) * 0.01));
  const stroke = Math.max(1, radius * 0.35);

  return (
    <svg
      className="genesis-markers"
      viewBox={`0 0 ${image_width} ${image_height}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={ptBR.workspaceCaso.genesisOverlayLabel}
      data-testid="genesis-overlay"
    >
      <g data-testid="genesis-extremities">
        {genesis.extremities.map((p, idx) => (
          <circle
            key={idx}
            cx={p.x}
            cy={p.y}
            r={radius}
            fill={GENESIS_TYPE_COLOR.extremities}
            fillOpacity={0.25}
            stroke={GENESIS_TYPE_COLOR.extremities}
            strokeWidth={stroke}
          >
            <title>{ptBR.workspaceCaso.genesisExtremities}</title>
          </circle>
        ))}
      </g>
      <g data-testid="genesis-junctions">
        {genesis.junctions.map((p, idx) => (
          <Diamond key={idx} point={p} size={radius * 1.3} stroke={stroke} />
        ))}
      </g>
      <g data-testid="genesis-stops">
        {genesis.estimated_stops.map((p, idx) => (
          <rect
            key={idx}
            x={p.x - radius}
            y={p.y - radius}
            width={radius * 2}
            height={radius * 2}
            fill={GENESIS_TYPE_COLOR.estimated_stops}
            fillOpacity={0.25}
            stroke={GENESIS_TYPE_COLOR.estimated_stops}
            strokeWidth={stroke}
          >
            <title>{ptBR.workspaceCaso.genesisStops}</title>
          </rect>
        ))}
      </g>
    </svg>
  );
}
